/**
 * Context Binding Engine — text-aware + range-aware coercions.
 *
 * THIS IS THE CLAUDE.md §4 1000×-ERROR RISK CLASS. A unit / currency
 * mis-parse ($/MWh vs $/kWh, $/MW vs $/kW, M vs B, total vs per-MW) is a
 * 1000× regression. Every coercion here:
 *   - returns `undefined` for anything it cannot type CLEANLY (never
 *     guesses — mirrors `coerceClaimedValue`),
 *   - NEVER collapses a range to a scalar (no midpoint — CLAUDE.md §2),
 *   - sniffs the unit token from the source text and normalises at the
 *     boundary (GW→MW, ha→acres; keeps $/MWh and $/kWh DISTINCT),
 *   - carries a `qualifier` ('approximate'/'at-least'/'at-most') WITHOUT
 *     fabricating precision ("~1.5 MW" stays a scalar + qualifier, NOT a
 *     band).
 *
 * Pure / synchronous. No I/O.
 *
 * Conversion constants are NAMED (CLAUDE.md §4) and mirror the existing
 * `unitNormalize.ts` table so the two stay byte-consistent.
 */
import { detectNegationPolarity } from '../../lib/sed/negationPolarity';
import type {
  CbeBoolValue,
  CbeDatePrecision,
  CbeDateValue,
  CbeEnumValue,
  CbeMultiValue,
  CbeProvenance,
  CbeQualifier,
  CbeRangeValue,
  CbeScalarValue,
} from './cbeTypes';

/* ── Named conversion constants (mirror unitNormalize.ts) ─────────────── */

/** 1 GW = 1000 MW. */
export const GW_TO_MW = 1000;
/** 1 hectare = 2.47105 acres. */
export const HA_TO_ACRES = 2.47105;
/** $1M = $1e6. */
export const MILLION = 1_000_000;
/** $1B = $1e9. */
export const BILLION = 1_000_000_000;

/* ── Shared parsing primitives ────────────────────────────────────────── */

/**
 * A raw numeric token + its trailing currency/SI scale suffix, if any.
 * Used by the currency parser so "$3.0-3.6B" applies the B multiplier to
 * BOTH endpoints from the single trailing suffix.
 */
interface ScaledNumber {
  readonly value: number;
  /** Multiplier already applied (1, 1e6, 1e9). */
  readonly scale: number;
}

/** Parse a bare number, tolerating thousands separators + leading `$`. */
function parseBareNumber(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Apply an M / B / K scale suffix found in `suffix` to `n`. Returns the
 * scaled number + the scale applied. Unknown suffix ⇒ scale 1.
 */
function applyScaleSuffix(n: number, suffix: string): ScaledNumber {
  const s = suffix.trim().toLowerCase();
  if (/^b(n|illion)?$/.test(s)) return { value: n * BILLION, scale: BILLION };
  if (/^m(m|illion)?$/.test(s)) return { value: n * MILLION, scale: MILLION };
  if (/^k$/.test(s)) return { value: n * 1_000, scale: 1_000 };
  return { value: n, scale: 1 };
}

/** Coerce an arbitrary fact value to a string for span-aware parsing. */
function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return '';
}

/**
 * Detect an approximate qualifier ("~", "approx", "around", "about",
 * "roughly", "circa") on a value string. Lowers precision WITHOUT inventing
 * a band.
 */
function detectApproximate(text: string): boolean {
  return /[~≈]|approx|approximate|around|about|roughly|circa|±/i.test(text);
}

/* ── Unit normalisation ───────────────────────────────────────────────── */

/**
 * Normalise a sniffed unit token to the engine's canonical form. Keeps
 * `$/MWh` and `$/kWh` DISTINCT (they are 1000× apart — never merge).
 * Returns the input trimmed when no rule applies.
 */
export function unitNormalize(rawUnit: string | undefined): string | undefined {
  if (!rawUnit) return undefined;
  const u = rawUnit.trim();
  if (u === '') return undefined;
  const lower = u.toLowerCase();
  // Power.
  if (/^gw$/.test(lower)) return 'MW'; // value converted separately via GW_TO_MW
  if (/^mw$/.test(lower)) return 'MW';
  if (/^kw$/.test(lower)) return 'kW';
  if (/^mva$/.test(lower)) return 'MVA';
  // Area.
  if (/^(ha|hectares?)$/.test(lower)) return 'acres'; // value converted via HA_TO_ACRES
  if (/^(ac|acres?)$/.test(lower)) return 'acres';
  // Energy price — DISTINCT scales, never merged.
  if (/^\$?\/?mwh$/.test(lower) || lower === '$/mwh') return '$/MWh';
  if (/^\$?\/?kwh$/.test(lower) || lower === '$/kwh') return '$/kWh';
  // Per-capacity cost — DISTINCT scales, never merged.
  if (lower === '$/mw') return '$/MW';
  if (lower === '$/kw') return '$/kW';
  return u;
}

/**
 * Convert a numeric `value` from its source `unit` into the canonical unit.
 * Only the magnitude-changing conversions (GW→MW, ha→acres) actually move
 * the number; everything else is identity. Returns the converted value +
 * the canonical unit.
 *
 * EXPORTED so the grounding fact store reuses this one conversion table when
 * it corroborates a bare-number `claimedValue` against a candidate's
 * unit-anchored `valuePattern` — unit handling has exactly one SSOT.
 */
export function convertToCanonical(value: number, rawUnit: string | undefined): { value: number; unit: string | undefined } {
  const lower = (rawUnit ?? '').trim().toLowerCase();
  if (/^gw$/.test(lower)) return { value: value * GW_TO_MW, unit: 'MW' };
  if (/^(ha|hectares?)$/.test(lower)) return { value: value * HA_TO_ACRES, unit: 'acres' };
  return { value, unit: unitNormalize(rawUnit) };
}

/**
 * Find the unit token IMMEDIATELY following the numeric `value` in `span`,
 * if any. Used so a bare-number claimedValue (`125`) inherits the unit
 * stated in its source span ("125 MW") — read from the span, never
 * fabricated. Returns `undefined` when the value (or an adjacent unit)
 * isn't found. Conservative: only the unit DIRECTLY after the value token
 * counts (optional single space), so a distant unit can't be mis-bound.
 *
 * EXPORTED for the same reason as {@link convertToCanonical}: the grounding
 * fact store must not re-implement unit sniffing.
 */
export function sniffUnitAdjacentInSpan(value: number, span: string): string | undefined {
  if (!span || !Number.isFinite(value)) return undefined;
  const tol = Math.max(1e-9, Math.abs(value) * 1e-6);
  const re = /(-?\d+(?:\.\d+)?)\s?(%|gw|mw|kw|mva|ha|hectares?|acres?|ac)\b/gi;
  for (let m = re.exec(span); m !== null; m = re.exec(span)) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && Math.abs(n - value) <= tol) return m[2];
  }
  return undefined;
}

/* ── Range detection ──────────────────────────────────────────────────── */

/** Hyphen / en-dash / "to" range separators. */
const RANGE_SEP = /\s*(?:-|–|—|to)\s*/i;

/**
 * Does the text express a (closed or open) numeric range? Used to ROUTE a
 * fact: a ranged fact must NEVER be coerced to a scalar.
 *
 * Closed: "10-12", "$3.0-3.6B", "100 to 200".
 * Open:   ">200", "<10", "at least 200", "up to 10".
 *
 * Guard: a slash-date ("3/31/2027") and a per-unit token ("$/MWh") are NOT
 * ranges. An approximate single value ("~1.5") is NOT a range.
 */
export function looksLikeRange(text: string): boolean {
  const t = text.trim();
  if (t === '') return false;
  // Open-ended.
  if (/(^|\s)(?:>|≥|<|≤|at\s+least|no\s+less\s+than|minimum\s+of|up\s+to|at\s+most|no\s+more\s+than|maximum\s+of)\b/i.test(t)) {
    if (/\d/.test(t)) return true;
  }
  if (/\d\s*\+(?!\d)/.test(t)) return true; // "200+"
  // Closed: a digit, a range separator, then a digit — but reject if the
  // separator is part of a slash-date or a per-unit "/" token.
  const closed = /(-?\d+(?:\.\d+)?)\s*(?:-|–|—|\bto\b)\s*(-?\d+(?:\.\d+)?)/i;
  if (closed.test(t)) return true;
  return false;
}

/* ── toNumber ─────────────────────────────────────────────────────────── */

/**
 * Coerce a single CLEAN scalar number from a fact value + source text.
 * Returns `undefined` when:
 *   - the text expresses a RANGE (caller must use {@link toRange}),
 *   - the text is a multi "N x M" form (caller must use {@link toMulti}),
 *   - no clean number can be parsed.
 *
 * Reads the source text to:
 *   - sniff + convert the unit (GW→MW, ha→acres),
 *   - strip a trailing "%" ("83.3%" → 83.3),
 *   - detect an approximate qualifier ("~1.5 MW" → scalar 1.5 +
 *     qualifier:'approximate' + LOWERED confidence; NEVER a band).
 *
 * @param value         the fact's already-parsed scalar (may be a string).
 * @param sourceText    the verbatim span (for unit/qualifier sniffing).
 * @param baseConfidence seed confidence (extractor confidence).
 * @param provenance    originating tag + span.
 */
export function toNumber(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeScalarValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  // Route away ranges + multis.
  if (looksLikeRange(text)) return undefined;
  if (/\d\s*[x×]\s*\d/i.test(text)) return undefined;

  const approximate = detectApproximate(text);

  // Pull the FIRST numeric token + any adjacent unit.
  const m = text.match(/(-?\d+(?:\.\d+)?)\s*(%|gw|mw|kw|mva|ha|hectares?|acres?|ac\b)?/i);
  if (!m) return undefined;
  const n = parseBareNumber(m[1]!);
  if (n === null) return undefined;

  // Unit token adjacent to the value IN THE VALUE STRING; fall back to the
  // unit adjacent to the SAME numeric token in the source span (so a bare
  // numeric claimedValue like `125` with span "125 MW capacity" still picks
  // up MW — read from the span, never invented).
  const unitToken = m[2] ?? sniffUnitAdjacentInSpan(n, sourceText);
  let outValue = n;
  let outUnit: string | undefined;

  if (unitToken === '%') {
    outUnit = '%';
  } else if (unitToken) {
    const conv = convertToCanonical(n, unitToken);
    outValue = conv.value;
    outUnit = conv.unit;
  }

  const qualifier: CbeQualifier | undefined = approximate ? 'approximate' : undefined;
  // Approximate lowers confidence by 1 step (does not fabricate precision).
  const confidence = approximate ? Math.max(0, baseConfidence - 1) : baseConfidence;

  return {
    kind: 'scalar',
    value: outValue,
    unit: outUnit,
    qualifier,
    confidence,
    provenance,
  };
}

/* ── toMulti ──────────────────────────────────────────────────────────── */

/**
 * Coerce a "N x M [unit]" multi form ("4 x 150 MVA" → count 4, perUnit 150,
 * total 600, unit MVA). `total = count × perUnit` is COMPUTED. Returns
 * `undefined` when the text isn't a clean multi.
 */
export function toMulti(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeMultiValue | undefined {
  const text = asText(value).trim();
  const m = text.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(gw|mw|kw|mva)?/i);
  if (!m) return undefined;
  const count = parseBareNumber(m[1]!);
  const perRaw = parseBareNumber(m[2]!);
  if (count === null || perRaw === null) return undefined;
  if (!Number.isInteger(count) || count <= 0) return undefined;

  const conv = m[3] ? convertToCanonical(perRaw, m[3]) : { value: perRaw, unit: undefined };
  const perUnit = conv.value;
  const total = count * perUnit;
  if (!Number.isFinite(total)) return undefined;

  return {
    kind: 'multi',
    count,
    perUnit,
    total,
    unit: conv.unit,
    confidence: baseConfidence,
    provenance,
  };
}

/* ── toRange ──────────────────────────────────────────────────────────── */

/**
 * Sniff a per-unit / currency / area unit token off a range string. Returns
 * the canonical unit and a multiplier to apply to the endpoints (1, 1e6,
 * 1e9 for currency-scale suffixes). Critically distinguishes:
 *   - "$10-12M/MW" → unit '$/MW', endpoints scaled ×1e6 (per-MW range).
 *   - "$3.0-3.6B"  → unit '$',     endpoints scaled ×1e9.
 *   - "1-5 MW"     → unit 'MW',    no scale.
 *   - "100-200 FTE"→ unit 'FTE',   no scale.
 */
function sniffRangeUnit(text: string): { unit: string | undefined; scale: number; gwToMw: boolean; haToAcres: boolean } {
  const t = text.trim();
  // Currency per-capacity: "$10-12M/MW", "$/kW".
  const perCap = t.match(/(m|mm|b|bn|k)?\s*\/\s*(mw|kw)\b/i);
  if (perCap) {
    const scale = perCap[1] ? applyScaleSuffix(1, perCap[1]).scale : 1;
    const denom = perCap[2]!.toLowerCase() === 'mw' ? '$/MW' : '$/kW';
    return { unit: denom, scale, gwToMw: false, haToAcres: false };
  }
  // Energy price: "$60-70/MWh", "/kWh".
  const energy = t.match(/\/\s*(mwh|kwh)\b/i);
  if (energy) {
    return { unit: energy[1]!.toLowerCase() === 'mwh' ? '$/MWh' : '$/kWh', scale: 1, gwToMw: false, haToAcres: false };
  }
  // Currency magnitude with trailing scale: "$3.0-3.6B", "$10-12M".
  // The `(?![a-z])` lookahead stops the suffix from swallowing the first
  // letter of a UNIT word ("MW"/"MVA"/"million-of-acres" etc.) — the `m`
  // in "200 MW" must NOT be read as a million multiplier (1000× guard).
  const curScale = t.match(/(\d)\s*(b|bn|billion|m|mm|million|k)(?![a-z])/i);
  if (curScale && /\$/.test(t)) {
    return { unit: '$', scale: applyScaleSuffix(1, curScale[2]!).scale, gwToMw: false, haToAcres: false };
  }
  if (/\$/.test(t)) return { unit: '$', scale: 1, gwToMw: false, haToAcres: false };
  // Power.
  if (/\bgw\b/i.test(t)) return { unit: 'MW', scale: 1, gwToMw: true, haToAcres: false };
  if (/\bmw\b/i.test(t)) return { unit: 'MW', scale: 1, gwToMw: false, haToAcres: false };
  if (/\bmva\b/i.test(t)) return { unit: 'MVA', scale: 1, gwToMw: false, haToAcres: false };
  // Area.
  if (/\b(ha|hectares?)\b/i.test(t)) return { unit: 'acres', scale: 1, gwToMw: false, haToAcres: true };
  if (/\b(ac|acres?)\b/i.test(t)) return { unit: 'acres', scale: 1, gwToMw: false, haToAcres: false };
  // Headcount.
  if (/\bfte\b/i.test(t)) return { unit: 'FTE', scale: 1, gwToMw: false, haToAcres: false };
  // Time.
  if (/\b(years?|yrs?|yr)\b/i.test(t)) return { unit: 'years', scale: 1, gwToMw: false, haToAcres: false };
  if (/\b(months?|mos?)\b/i.test(t)) return { unit: 'months', scale: 1, gwToMw: false, haToAcres: false };
  return { unit: undefined, scale: 1, gwToMw: false, haToAcres: false };
}

/** Apply the sniffed magnitude conversions (GW→MW, ha→acres) to an endpoint. */
function applyMagnitude(n: number, su: ReturnType<typeof sniffRangeUnit>): number {
  let v = n * su.scale;
  if (su.gwToMw) v *= GW_TO_MW;
  if (su.haToAcres) v *= HA_TO_ACRES;
  return v;
}

/**
 * Coerce a stated range into `{min, max, unit}`. NEVER collapses to a
 * scalar — the midpoint is forbidden. Handles:
 *   - closed: "$10-12M/MW", "1-5 MW", "100-200 FTE", "$3.0-3.6B",
 *     "$60-70/MWh", "2-4 years".
 *   - open:   ">200" → `{min:200, max:null, qualifier:'at-least'}`,
 *             "<10"  → `{min:null, max:10,  qualifier:'at-most'}`,
 *             "200+" → `{min:200, max:null, qualifier:'at-least'}`.
 *
 * Returns `undefined` when no range can be parsed.
 */
export function toRange(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeRangeValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  const su = sniffRangeUnit(text);

  // ── Open-ended ─────────────────────────────────────────────────────
  // ">200", "≥ 200", "at least 200", "no less than 200", "minimum of 200".
  const gt = text.match(/(?:>|≥|at\s+least|no\s+less\s+than|minimum\s+of)\s*\$?\s*(\d+(?:\.\d+)?)\s*(b|bn|m|mm|k)?(?![a-z])/i);
  if (gt) {
    const n = parseBareNumber(gt[1]!);
    if (n === null) return undefined;
    const scaled = gt[2] ? applyScaleSuffix(n, gt[2]).value : applyMagnitude(n, su);
    return { kind: 'range', min: scaled, max: null, unit: su.unit, qualifier: 'at-least', confidence: baseConfidence, provenance };
  }
  // "200+".
  const plus = text.match(/(\d+(?:\.\d+)?)\s*\+(?!\d)/);
  if (plus) {
    const n = parseBareNumber(plus[1]!);
    if (n === null) return undefined;
    return { kind: 'range', min: applyMagnitude(n, su), max: null, unit: su.unit, qualifier: 'at-least', confidence: baseConfidence, provenance };
  }
  // "<10", "≤ 10", "up to 10", "at most 10", "no more than 10", "maximum of 10".
  const lt = text.match(/(?:<|≤|up\s+to|at\s+most|no\s+more\s+than|maximum\s+of)\s*\$?\s*(\d+(?:\.\d+)?)\s*(b|bn|m|mm|k)?(?![a-z])/i);
  if (lt) {
    const n = parseBareNumber(lt[1]!);
    if (n === null) return undefined;
    const scaled = lt[2] ? applyScaleSuffix(n, lt[2]).value : applyMagnitude(n, su);
    return { kind: 'range', min: null, max: scaled, unit: su.unit, qualifier: 'at-most', confidence: baseConfidence, provenance };
  }

  // ── Closed ─────────────────────────────────────────────────────────
  // "$10-12M/MW", "$3.0-3.6B", "1-5 MW", "100-200 FTE", "$60-70/MWh".
  const closed = text.match(/(-?\d+(?:\.\d+)?)\s*(?:-|–|—|\bto\b)\s*(-?\d+(?:\.\d+)?)/i);
  if (closed) {
    const a = parseBareNumber(closed[1]!);
    const b = parseBareNumber(closed[2]!);
    if (a === null || b === null) return undefined;
    const min = Math.min(applyMagnitude(a, su), applyMagnitude(b, su));
    const max = Math.max(applyMagnitude(a, su), applyMagnitude(b, su));
    if (!Number.isFinite(min) || !Number.isFinite(max)) return undefined;
    return { kind: 'range', min, max, unit: su.unit, confidence: baseConfidence, provenance };
  }
  return undefined;
}

/* ── toDate ───────────────────────────────────────────────────────────── */

const MONTHS: Readonly<Record<string, number>> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
  september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

/** Quarter → the FIRST month of that quarter (Q3 → month 7). */
const QUARTER_FIRST_MONTH: Readonly<Record<string, number>> = { '1': 1, '2': 4, '3': 7, '4': 10 };

/** Zero-pad a 1-2 digit number to 2 chars. */
function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Build an ISO yyyy-mm-dd, validating month/day ranges. */
function isoDate(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || year < 1900 || year > 2200) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Coerce a date string into `{iso, precision}`. NEVER fabricates precision:
 * a "Q3 2028" returns precision 'quarter' (iso normalised to the quarter's
 * first day); "Jan 2028" → 'month'; "3/31/2027" → 'day'.
 *
 * Handles:
 *   - slash dates  "M/D/YYYY", "MM/DD/YYYY", "M/YYYY" (→ month precision)
 *   - ISO          "2027-03-31", "2028-01"
 *   - quarter      "Q3 2028", "3Q2028"
 *   - month-year   "Jan 2028", "January 2028"
 *   - bare year    "2028"
 */
export function toDate(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeDateValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;

  const emit = (iso: string, precision: CbeDatePrecision): CbeDateValue => ({
    kind: 'date', iso, precision, confidence: baseConfidence, provenance,
  });

  // ISO full / partial: 2027-03-31, 2028-01.
  const isoM = text.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (isoM) {
    const y = Number(isoM[1]);
    const mo = Number(isoM[2]);
    const d = isoM[3] ? Number(isoM[3]) : 1;
    const iso = isoDate(y, mo, d);
    if (iso) return emit(iso, isoM[3] ? 'day' : 'month');
    return undefined;
  }

  // Slash dates: 3/31/2027, 03/31/2027, 3/2027.
  const slash = text.match(/^(\d{1,2})\/(?:(\d{1,2})\/)?(\d{4})$/);
  if (slash) {
    const mo = Number(slash[1]);
    const y = Number(slash[3]);
    if (slash[2] === undefined) {
      const iso = isoDate(y, mo, 1);
      return iso ? emit(iso, 'month') : undefined;
    }
    const d = Number(slash[2]);
    const iso = isoDate(y, mo, d);
    return iso ? emit(iso, 'day') : undefined;
  }

  // Quarter: "Q3 2028", "3Q2028", "Q3 '28" not supported (ambiguous year).
  const q = text.match(/(?:Q\s*([1-4])\s*[-/ ]?\s*(\d{4})|([1-4])\s*Q\s*(\d{4}))/i);
  if (q) {
    const qn = (q[1] ?? q[3])!;
    const y = Number(q[2] ?? q[4]);
    const mo = QUARTER_FIRST_MONTH[qn];
    if (mo) {
      const iso = isoDate(y, mo, 1);
      if (iso) return emit(iso, 'quarter');
    }
    return undefined;
  }

  // Month-year: "Jan 2028", "January 2028".
  const my = text.match(/^([A-Za-z]{3,9})\.?\s+(\d{4})$/);
  if (my) {
    const mo = MONTHS[my[1]!.toLowerCase()];
    const y = Number(my[2]);
    if (mo) {
      const iso = isoDate(y, mo, 1);
      if (iso) return emit(iso, 'month');
    }
    return undefined;
  }

  // Bare year: "2028".
  const yr = text.match(/^(\d{4})$/);
  if (yr) {
    const y = Number(yr[1]);
    const iso = isoDate(y, 1, 1);
    return iso ? emit(iso, 'year') : undefined;
  }

  return undefined;
}

/* ── toBool ───────────────────────────────────────────────────────────── */

/**
 * Coerce a boolean, reading negation polarity from the SOURCE SPAN (not
 * just the parsed value). A span containing "no/not/not yet/never" flips
 * the polarity to the negated reading so "permit NOT yet filed" resolves
 * to the correct boolean rather than the LLM's guess.
 *
 * Contract (mirrors `detectNegationPolarity`): a negation marker in the
 * span forces `false` for an affirmative-predicate field. Absent any
 * marker, we trust the parsed value when it is an explicit boolean / yes /
 * no; otherwise `undefined` (never guess).
 */
export function toBool(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeBoolValue | undefined {
  const emit = (b: boolean): CbeBoolValue => ({ kind: 'bool', value: b, confidence: baseConfidence, provenance });

  // Span negation is authoritative — a "not/no/never" marker means the
  // affirmative predicate is FALSE.
  if (sourceText && detectNegationPolarity(sourceText) === true) return emit(false);

  if (typeof value === 'boolean') return emit(value);
  const text = asText(value).trim().toLowerCase();
  if (text === '') return undefined;
  if (text === 'true' || text === 'yes' || text === 'y') return emit(true);
  if (text === 'false' || text === 'no' || text === 'n') return emit(false);
  return undefined;
}

/* ── toEnum ───────────────────────────────────────────────────────────── */

/** Negation guard for span-derived enum hits: a negation marker within a
 *  local window around the keyword match means the span DENIES the
 *  predicate ("the LGIA has not yet been executed") — emitting the member
 *  would fabricate an affirmative status the source explicitly denies.
 *  Window-scoped so an unrelated negation elsewhere in a long span does
 *  not kill a clean hit; when in doubt the hit is skipped (never guess).
 *
 *  The marker set mirrors `detectNegationPolarity` MINUS the bare word
 *  "none": for enum windows "none" is a status word in its own right
 *  ("compliance actions: none") rather than a negator of the adjacent
 *  keyword, and bare-"none" fabrication is separately prevented by
 *  excluding 'none' from the member span scan below. */
const ENUM_NEGATION_WINDOW = 60;
const ENUM_NEGATION_RE =
  /\b(no|not|not\s+yet|never|without|cannot|can't|won't|isn't|aren't|wasn't|weren't|hasn't|haven't|hadn't|doesn't|don't|didn't|unfiled|not\s+been|has\s+not|have\s+not|had\s+not|yet\s+to)\b/;

function spanHitNegated(span: string, matchIndex: number, matchLength: number): boolean {
  const start = Math.max(0, matchIndex - ENUM_NEGATION_WINDOW);
  const end = Math.min(span.length, matchIndex + matchLength + ENUM_NEGATION_WINDOW);
  const window = ` ${span.slice(start, end).toLowerCase().replace(/[’']/g, "'")} `;
  return ENUM_NEGATION_RE.test(window);
}

/**
 * Coerce an enum member — ONLY when a member's keyword is EXPLICITLY present
 * in the source span (or the parsed value matches a member directly). Never
 * fabricates a member from absence, and a negation marker near the keyword
 * match suppresses the hit (see {@link spanHitNegated}).
 *
 * @param members  the allowed enum values.
 * @param synonyms optional keyword→member map; a span containing a synonym
 *                 (case-insensitive, word-boundary) resolves to its member.
 */
export function toEnum(
  value: string | number | boolean | undefined,
  sourceText: string,
  members: readonly string[],
  baseConfidence: number,
  provenance: CbeProvenance,
  synonyms?: Readonly<Record<string, string>>,
): CbeEnumValue | undefined {
  const emit = (m: string): CbeEnumValue => ({ kind: 'enum', value: m, confidence: baseConfidence, provenance });

  // Direct value match (case-insensitive).
  const text = asText(value).trim().toLowerCase();
  if (text !== '') {
    const direct = members.find(m => m.toLowerCase() === text);
    if (direct) return emit(direct);
  }

  const span = (sourceText ?? '').toLowerCase();
  if (span === '') return undefined;

  // Synonym keyword match in the span — skipped when the local window
  // around the hit carries a negation marker.
  if (synonyms) {
    for (const [kw, member] of Object.entries(synonyms)) {
      if (!members.includes(member)) continue;
      const esc = kw.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const hit = new RegExp(`\\b${esc}\\b`, 'i').exec(span);
      if (hit && !spanHitNegated(span, hit.index, hit[0].length)) return emit(member);
    }
  }

  // Bare member keyword present in the span, same negation window. The
  // member 'none' is never span-scanned: the bare word "none" appearing
  // anywhere ("outstanding actions: none") would fabricate an absence
  // status — it only resolves via the direct value match above.
  for (const m of members) {
    if (m.toLowerCase() === 'none') continue;
    const esc = m.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const hit = new RegExp(`\\b${esc}\\b`, 'i').exec(span);
    if (hit && !spanHitNegated(span, hit.index, hit[0].length)) return emit(m);
  }

  return undefined;
}

/* ── toPercent / toYears ──────────────────────────────────────────────── */

/**
 * Coerce a percentage to its NUMERIC magnitude ("83.3%" → 83.3, "2%" → 2).
 * Returns a scalar carrying unit '%'. A range ("10-20%") routes to
 * {@link toRange}; here we return `undefined` for a range.
 */
export function toPercent(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeScalarValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  if (looksLikeRange(text)) return undefined;
  const m = text.match(/(-?\d+(?:\.\d+)?)\s*%?/);
  if (!m) return undefined;
  const n = parseBareNumber(m[1]!);
  if (n === null) return undefined;
  const approximate = detectApproximate(text);
  return {
    kind: 'scalar',
    value: n,
    unit: '%',
    qualifier: approximate ? 'approximate' : undefined,
    confidence: approximate ? Math.max(0, baseConfidence - 1) : baseConfidence,
    provenance,
  };
}

/**
 * Coerce a tenor in years ("15-yr" → 15, "15 years" → 15). A range
 * ("2-4 years") routes to {@link toRange}; here we return `undefined`.
 */
export function toYears(
  value: string | number | boolean | undefined,
  sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): CbeScalarValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  if (looksLikeRange(text)) return undefined;
  const m = text.match(/(-?\d+(?:\.\d+)?)\s*[-\s]*(?:years?|yrs?|yr)\b/i)
    ?? text.match(/^(-?\d+(?:\.\d+)?)$/);
  if (!m) return undefined;
  const n = parseBareNumber(m[1]!);
  if (n === null) return undefined;
  return { kind: 'scalar', value: n, unit: 'years', confidence: baseConfidence, provenance };
}

/* ── toString (complex passthrough) ───────────────────────────────────── */

/**
 * Coerce a free-form string descriptor (a `complex` value). Trims; returns
 * `undefined` for empty / non-string-coercible input. Never parses numbers.
 */
export function toComplexString(
  value: string | number | boolean | undefined,
  _sourceText: string,
  baseConfidence: number,
  provenance: CbeProvenance,
): import('./cbeTypes').CbeComplexValue | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  return { kind: 'complex', value: text, confidence: baseConfidence, provenance };
}
